import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ColDef, GridReadyEvent } from "ag-grid-community";
import { Plus } from "lucide-react";
import { AgGridTable, ConfirmDialog, LoadingState } from "components";
import type {
  QueryState,
} from "components/ui-common/AgGridTable/AgGridTable";
import { useBankAccount, useBankAccountStore } from "hooks";
import {
  BankAccount,
  BankAccountForm,
  BankAccountQuery,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";

const defaultFormValues: BankAccountForm = {
  bank_name: "",
  account_number: "",
  account_name: "",
  branch: "",
  note: "",
};

const BankAccounts: React.FC = () => {
  const gridApiRef = useRef<GridReadyEvent["api"] | null>(null);
  const addRowRef = useRef<BankAccount | null>(null);
  const [addRowVersion, setAddRowVersion] = useState(0);
  const [queryParams, setQueryParams] = useState<BankAccountQuery>({
    page: 1,
    pageSize: 20,
  });
  const [showInlineAddRow, setShowInlineAddRow] = useState<any>("");
  const [confirmRow, setConfirmRow] = useState<BankAccount | null>(null);

  const {
    rows,
    setRows,
    upsertRow,
    removeRow,
    editingId,
    setEditingId,
    isLoading,
    setLoading,
  } = useBankAccountStore();
  const {
    fetchList,
    createBankAccount,
    updateBankAccount,
    deleteBankAccount,
  } = useBankAccount();

  const {
    data: bankAccountData,
    isFetching,
    refetch,
  } = fetchList(queryParams, true);

  useEffect(() => {
    if (bankAccountData?.data) {
      setRows(bankAccountData.data as BankAccount[]);
    }
  }, [bankAccountData, setRows]);

  useEffect(() => {
    setLoading(
      Boolean(
        isFetching ||
          createBankAccount.isPending ||
          updateBankAccount.isPending ||
          deleteBankAccount.isPending,
      ),
    );
  }, [
    createBankAccount.isPending,
    deleteBankAccount.isPending,
    isFetching,
    setLoading,
    updateBankAccount.isPending,
  ]);

  const columns = useMemo<ColDef<BankAccount>[]>(() => {
    return [
      {
        headerName: "Ngân hàng",
        field: "bank_name",
        minWidth: 180,
        flex: 1,
      },
      {
        headerName: "Số tài khoản",
        field: "account_number",
        minWidth: 160,
      },
      {
        headerName: "Chủ tài khoản",
        field: "account_name",
        minWidth: 180,
        flex: 1,
      },
      {
        headerName: "Chi nhánh",
        field: "branch",
        minWidth: 160,
        flex: 1,
      },
      {
        headerName: "Ghi chú",
        field: "note",
        minWidth: 200,
        flex: 1,
      },
    ];
  }, []);

  const normalizePayload = useCallback(
    (row: Partial<BankAccount>): BankAccountForm => ({
      bank_name: String(row.bank_name ?? "").trim(),
      account_number: String(row.account_number ?? "").trim(),
      account_name: String(row.account_name ?? "").trim(),
      branch: row.branch ? String(row.branch).trim() : "",
      note: row.note ? String(row.note).trim() : "",
    }),
    [],
  );

  type PersistMeta = {
    pinned?: boolean;
    onRevert?: () => void;
    onSuccess?: () => void;
    refetch?: boolean;
  };

  const resetInlineAddRow = useCallback(() => {
    addRowRef.current = {
      ...defaultFormValues,
    } as BankAccount;
    setAddRowVersion((v) => v + 1);
  }, []);

  const persistRow = useCallback(
    (row: Partial<BankAccount>, meta?: PersistMeta) => {
      const payload = normalizePayload(row);

      if (!payload.bank_name || !payload.account_number || !payload.account_name) {
        SnackbarUtils.error(
          "Ngân hàng, Số tài khoản và Chủ tài khoản là bắt buộc",
        );
        meta?.onRevert?.();
        return;
      }

      const shouldCreate = meta?.pinned || !row.id;
      if (shouldCreate) {
        createBankAccount.mutate(payload, {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(res.data as BankAccount);
            }
            resetInlineAddRow();
            if (meta?.refetch !== false) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Thêm tài khoản ngân hàng không thành công");
            meta?.onRevert?.();
          },
        });
        return;
      }

      if (!row.id) {
        SnackbarUtils.error("Không xác định được tài khoản ngân hàng cần cập nhật");
        meta?.onRevert?.();
        return;
      }

      updateBankAccount.mutate(
        { id: row.id, ...payload },
        {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(res.data as BankAccount);
            } else {
              upsertRow({ ...(row as BankAccount), ...payload });
            }
            if (meta?.refetch) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Cập nhật tài khoản ngân hàng không thành công");
            meta?.onRevert?.();
          },
        },
      );
    },
    [
      createBankAccount,
      normalizePayload,
      refetch,
      resetInlineAddRow,
      updateBankAccount,
      upsertRow,
    ],
  );

  const handleSaveRow = useCallback(
    (row: BankAccount, meta?: { pinned?: boolean }) => {
      persistRow(row, {
        pinned: meta?.pinned,
        refetch: meta?.pinned || !row.id,
      });
      setShowInlineAddRow("");
    },
    [persistRow],
  );

  const handleEditRow = useCallback(
    (params) => {
      setShowInlineAddRow("edit");
      setEditingId(params.data.id);
      setTimeout(
        () =>
          gridApiRef.current?.startEditingCell({
            rowIndex: params.node.rowIndex,
            colKey: "bank_name",
          }),
        300,
      );
    },
    [setEditingId],
  );

  const handleDeleteRow = useCallback((row: BankAccount) => {
    setConfirmRow(row);
    setShowInlineAddRow("");
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!confirmRow) return;
    deleteBankAccount.mutate(confirmRow.id, {
      onSuccess: () => {
        removeRow(confirmRow.id);
        if (editingId === confirmRow.id) {
          setEditingId(null);
        }
        setConfirmRow(null);
      },
      onError: () => {
        SnackbarUtils.error("Xóa tài khoản ngân hàng không thành công");
      },
    });
  }, [
    confirmRow,
    deleteBankAccount,
    editingId,
    removeRow,
    setEditingId,
  ]);

  const toggleInlineAddRow = useCallback(() => {
    setEditingId(null);
    gridApiRef.current?.stopEditing();
    setShowInlineAddRow((prev) => {
      const next = !prev ? "add" : "";
      if (next) {
        resetInlineAddRow();
      } else {
        addRowRef.current = null;
        setAddRowVersion((v) => v + 1);
      }
      return next;
    });
  }, [resetInlineAddRow, setEditingId]);

  const handleQueryChange = useCallback(
    (query: QueryState) => {
      setQueryParams((prev) => {
        const next = {
          ...prev,
          page: query.pagination?.page ?? prev.page,
          pageSize: query.pagination?.pageSize ?? prev.pageSize,
          sortModel: query.sortModel,
          filterModel: query.filterModel,
        };
        const unchanged =
          next.page === prev.page &&
          next.pageSize === prev.pageSize &&
          next.sortModel === prev.sortModel &&
          next.filterModel === prev.filterModel;
        return unchanged ? prev : next;
      });
    },
    [setQueryParams],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      flex: 1,
      minWidth: 140,
    }),
    [],
  );

  const inlineAddRowData = useMemo(
    () => (showInlineAddRow ? (addRowRef.current ?? undefined) : undefined),
    [showInlineAddRow, addRowVersion],
  );

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">
          Quản lý tài khoản ngân hàng
        </h1>
        <p className="text-sm text-gray-600">
          Danh sách tài khoản ngân hàng dùng cho các chứng từ thu/chi tiền gửi.
        </p>
      </div>

      <div className="relative bg-white border border-gray-200 shadow-sm rounded-lg p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Danh sách tài khoản ngân hàng
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggleInlineAddRow}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Plus className="text-green-700" />
            </button>
          </div>
        </div>
        <AgGridTable
          rows={rows}
          columns={columns}
          height={520}
          autoHeight={false}
          totalPages={bankAccountData?.totalPage}
          showAddRow={showInlineAddRow}
          addRowData={inlineAddRowData}
          defaultColDef={defaultColDef}
          onEdit={handleEditRow}
          onDelete={handleDeleteRow}
          onSave={handleSaveRow}
          onAfterActions={() => {
            setShowInlineAddRow("");
            setEditingId(null);
          }}
          getRowId={(params) => String(params.data.id)}
          onQueryChange={handleQueryChange}
          onGridReady={(event) => {
            gridApiRef.current = event.api;
          }}
        />
        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang cập nhật danh sách tài khoản ngân hàng, vui lòng chờ giây lát."
          />
        )}
      </div>
      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa tài khoản ngân hàng?"
        description={`Bạn chắc chắn muốn xóa tài khoản ngân hàng "${confirmRow?.account_number ?? ""}"${confirmRow?.bank_name ? ` - ${confirmRow.bank_name}` : ""
          }? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={deleteBankAccount.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default BankAccounts;
